import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { JobMessages } from 'src/intl/JobMessages';
import { Organization } from 'src/models/organization';
import { BundleMessages } from '../../intl/BundleMessages';
import { InputSelect } from 'src/components/reusable-components/InputSelect';

interface FilterBundlesProps {
  /**
   * The Organizations options.
   */
  organizations: Organization[];
  /**
   * The site names for a bundle.
   */
  siteNames: string[];
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal and reset the state.
   */
  onClose: () => void;
  /**
   * Triggers the request to close the modal and save the state object.
   */
  onSave: (e: FilterBundlesModalState) => void;
}

export interface FilterBundlesModalState {
  organization: string | undefined;
  siteName: string | undefined;
}

const filterInitialState: FilterBundlesModalState = {
  organization: undefined,
  siteName: undefined,
};

export const FilterBundlesModal: React.FC<FilterBundlesProps> = ({
  organizations,
  siteNames,
  onClose,
  isOpen,
  onSave,
}) => {
  const [filterSelections, setFilterSelections] = useState<FilterBundlesModalState>(filterInitialState);
  const { formatMessage } = useIntl();

  useEffect(() => {
    setFilterSelections(filterInitialState);
  }, [isOpen]);

  const organizationSelectOptions = organizations.map(org => {
    return {
      label: org.orgName,
      value: org._id,
    };
  });

  const siteNameSelectOptions = siteNames.map(siteName => {
    return {
      label: siteName,
      value: siteName,
    };
  });

  return (
    <IonModal isOpen={isOpen} onDidDismiss={() => onClose()}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{formatMessage(BundleMessages.filter)}</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={() => onClose()}>
              <IonIcon size="large" color="dark" slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <InputSelect<string>
          label={formatMessage(ControllerMessages.organization)}
          options={organizationSelectOptions}
          value={filterSelections.organization}
          onChange={orgId => setFilterSelections({ ...filterSelections, organization: orgId })}
          placeholder={formatMessage(JobMessages.selectAnOrganization)}
          id="filter-bundles-select-organization"
        />

        <InputSelect<string>
          label={formatMessage(BundleMessages.siteName)}
          options={siteNameSelectOptions}
          value={filterSelections.siteName}
          onChange={siteName => setFilterSelections({ ...filterSelections, siteName })}
          placeholder={formatMessage(BundleMessages.selectASiteName)}
          id="filter-bundles-select-site-name"
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton onClick={() => onSave(filterSelections)} fill="solid" expand="block" color="primary">
            {formatMessage(BundleMessages.filterBundles)}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};
